/**
 * Active-entity context — which client (entity) you're viewing.
 * @file src/web/features/admin/lib/company-context.tsx
 *
 * Loads the professional's entities once, holds the selected one (persisted in
 * localStorage), and exposes it to the data pages (Dashboard, Ledgers,
 * Day Book) so the sidebar entity dropdown scopes everything. `entityId`
 * of null means "all entities" (the professional-wide view). Entity (client)
 * logins only ever see their own entity.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { adminFetchJson } from './admin-api';

export interface EntityLite { id: string; name: string }

interface Ctx {
  entities: EntityLite[];
  entityId: string | null;
  setEntityId: (id: string | null) => void;
  loading: boolean;
}

const EntityContext = createContext<Ctx>({ entities: [], entityId: null, setEntityId: () => {}, loading: true });
const KEY = 'oddits:entityId';

export function CompanyProvider({ children }: { children: ReactNode }) {
  const [entities, setEntities] = useState<EntityLite[]>([]);
  const [entityId, setId] = useState<string | null>(() =>
    typeof window !== 'undefined' ? localStorage.getItem(KEY) : null,
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    adminFetchJson<{ items: EntityLite[] }>('/api/entities', { query: { status: 'active' } })
      .then((d) => {
        if (cancel) return;
        setEntities(d.items);
        // Drop a stale selection; a lone entity auto-selects, else null (= all).
        setId((prev) => (prev && d.items.some((e) => e.id === prev) ? prev : (d.items.length === 1 ? d.items[0].id : null)));
      })
      .catch(() => {})
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, []);

  const setEntityId = (id: string | null) => {
    if (id) localStorage.setItem(KEY, id); else localStorage.removeItem(KEY);
    setId(id);
  };

  return (
    <EntityContext.Provider value={{ entities, entityId, setEntityId, loading }}>
      {children}
    </EntityContext.Provider>
  );
}

export const useCompany = () => useContext(EntityContext);
